import React from 'react';
import {
  Autocomplete,
  Divider,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { TokenAwareTextField as TextField } from './TokenAwareTextField';
import {
  formatPeopleToSchedule,
  personaSelectionRef,
  WorkshopPrepConfig,
  WorkshopPrepPersonaSelection,
  WorkshopPrepPriorityLabels,
} from '../../../Campaigns/workshopPrepConfig';
import { PersonaRole } from '../../../../support/campaigns';
import { useCustomerPersonas, useWorkshopTeamRoles } from '../../../../hooks/campaign-hooks';

/** The priority enum choices offered per selected persona (wording matches `PersonasEditor`). */
const PERSONA_ROLES: { value: PersonaRole; label: string }[] = [
  { value: 'must', label: 'Must have' },
  { value: 'should', label: 'Should have' },
  { value: 'nice', label: 'Nice to have' },
];

/**
 * The copy + Webex-defaults editor for the workshop-prep ("Schedule") step, split into the same
 * three sections the SE sees at runtime (`WorkshopPrepPanel`): the Cisco account team, the customer
 * personas, and Webex scheduling. It edits ONLY the step-scoped `metadata.workshopPrepConfig`
 * (headings, persona-priority labels, recording notice, meeting lengths, contact titles, meeting
 * title template) PLUS which of the campaign's team roles / personas this workshop selects and each
 * selected persona's priority. The role/persona *catalogues* themselves stay campaign-level reference
 * objects, edited in the References tab (`TeamRolesEditor` / `PersonasEditor`); here the author only
 * picks a subset and prioritizes it. See `Campaigns/workshopPrepConfig.ts` for the shape and defaults.
 *
 * Controlled: `value` is the fully-resolved config (defaults already merged, so every field is
 * populated) and `onChange` receives the whole next config. The parent binds it through a single
 * react-hook-form `Controller` so a save writes the full authored object — DB-driven, no blanks.
 */

/** Parse a comma-separated list of positive integers (meeting lengths, in minutes). */
function parseDurations(text: string): number[] {
  return text
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isFinite(n) && n > 0);
}

export function WorkshopPrepConfigFields({
  section,
  value,
  onChange,
  disabled,
  campaignId,
}: {
  section: 'team' | 'personas' | 'schedule';
  value: WorkshopPrepConfig;
  onChange: (next: WorkshopPrepConfig) => void;
  disabled?: boolean;
  /** The campaign whose persona/role catalogue the team/personas sections select from. */
  campaignId: number;
}): JSX.Element {
  // The campaign's persona/role catalogue drives the team + personas multi-selects (managed in the
  // References tab; here the step only selects a subset). Both hooks are cheap and react-query-cached,
  // so calling them for every section is fine; only the relevant section renders their options.
  const { personas: personaOptions } = useCustomerPersonas(campaignId);
  const { roles: roleOptions } = useWorkshopTeamRoles(campaignId);
  // Local text buffers for the free-form list fields so intermediate typing (trailing commas,
  // blank lines) is preserved while the parsed arrays flow up on every change. Seeded once from
  // the incoming config; the form edits one step at a time, so no external resync is needed.
  const [durationsText, setDurationsText] = React.useState(() =>
    value.schedule.durationOptions.join(', '),
  );

  const setTeam = (patch: Partial<WorkshopPrepConfig['team']>) =>
    onChange({ ...value, team: { ...value.team, ...patch } });
  const setPersonas = (patch: Partial<WorkshopPrepConfig['personas']>) =>
    onChange({ ...value, personas: { ...value.personas, ...patch } });
  const setPriority = (patch: Partial<WorkshopPrepPriorityLabels>) =>
    onChange({
      ...value,
      personas: { ...value.personas, priorityLabels: { ...value.personas.priorityLabels, ...patch } },
    });
  const setSchedule = (patch: Partial<WorkshopPrepConfig['schedule']>) =>
    onChange({ ...value, schedule: { ...value.schedule, ...patch } });

  // --- Team-role selection. `undefined` means "all catalogue roles"; the first toggle materializes
  // the effective set into an explicit, authoritative array. ---
  const selectedRoleNames = value.team.roleRefs ?? roleOptions.map((r) => r.name);

  // --- Persona selection + per-persona priority. Same "undefined = all (at each catalogue `role`)"
  // rule; selecting/priority edits write the explicit array. ---
  const selectedPersonas: WorkshopPrepPersonaSelection[] =
    value.personas.selected ??
    personaOptions.map((p) => ({ ref: personaSelectionRef(p), priority: p.role }));
  const isPersonaSelected = (ref: string) => selectedPersonas.some((s) => s.ref === ref);
  const personaPriorityFor = (ref: string): PersonaRole =>
    selectedPersonas.find((s) => s.ref === ref)?.priority ?? 'should';
  const setPersonaPriority = (ref: string, priority: PersonaRole) =>
    setPersonas({
      selected: selectedPersonas.map((s) => (s.ref === ref ? { ...s, priority } : s)),
    });

  // The selected personas resolved to catalogue labels (falling back to the stored ref for a row that
  // is no longer in the catalogue), for the schedule section's read-only roster preview.
  const selectedPersonaLabels = selectedPersonas.map(
    (s) => personaOptions.find((p) => personaSelectionRef(p) === s.ref)?.label ?? s.ref,
  );
  const peopleToScheduleSummary = formatPeopleToSchedule(selectedRoleNames, selectedPersonaLabels);

  if (section === 'team') {
    return (
      <Stack spacing={2}>
        <TextField
          label="Section heading"
          size="small"
          fullWidth
          disabled={disabled}
          value={value.team.title}
          onChange={(e) => setTeam({ title: e.target.value })}
          helperText="Heading over the Cisco account-team block (default “Account Team”)."
        />
        <TextField
          label="Section description"
          size="small"
          fullWidth
          multiline
          minRows={2}
          disabled={disabled}
          value={value.team.description}
          onChange={(e) => setTeam({ description: e.target.value })}
          helperText="Lead paragraph under the heading."
        />

        <Divider flexItem />
        <Typography variant="subtitle2">Roles offered on this workshop</Typography>
        <Typography variant="caption" color="text.secondary">
          Pick which of the campaign&apos;s team roles the SE can tag on this workshop. Manage the role
          catalogue itself in the References tab.
        </Typography>
        {roleOptions.length === 0 ? (
          <Typography variant="caption" color="warning.main">
            No team roles defined yet — add them in the References tab.
          </Typography>
        ) : (
          <Autocomplete
            multiple
            size="small"
            disableCloseOnSelect
            disabled={disabled}
            options={roleOptions.map((r) => r.name)}
            value={selectedRoleNames}
            onChange={(_e, next) => setTeam({ roleRefs: next })}
            renderInput={(params) => (
              <TextField {...params} label="Roles offered" placeholder="Add a role" />
            )}
          />
        )}
      </Stack>
    );
  }

  if (section === 'personas') {
    return (
      <Stack spacing={2}>
        <TextField
          label="Section heading"
          size="small"
          fullWidth
          disabled={disabled}
          value={value.personas.title}
          onChange={(e) => setPersonas({ title: e.target.value })}
          helperText="Heading over the customer-persona block (default “Get the right people in the room”)."
        />
        <TextField
          label="Section description"
          size="small"
          fullWidth
          multiline
          minRows={2}
          disabled={disabled}
          value={value.personas.description}
          onChange={(e) => setPersonas({ description: e.target.value })}
          helperText="Lead paragraph under the heading."
        />
        <TextField
          label="Priority label — “Must have” personas"
          size="small"
          fullWidth
          disabled={disabled}
          value={value.personas.priorityLabels.must}
          onChange={(e) => setPriority({ must: e.target.value })}
          helperText="How a persona tagged “must” shows next to its name (default “Highly Recommended”)."
        />
        <TextField
          label="Priority label — “Should have” personas"
          size="small"
          fullWidth
          disabled={disabled}
          value={value.personas.priorityLabels.should}
          onChange={(e) => setPriority({ should: e.target.value })}
          helperText="Default “Recommended”."
        />
        <TextField
          label="Priority label — “Nice to have” personas"
          size="small"
          fullWidth
          disabled={disabled}
          value={value.personas.priorityLabels.nice}
          onChange={(e) => setPriority({ nice: e.target.value })}
          helperText="Default “Optional”."
        />

        <Divider flexItem />
        <Typography variant="subtitle2">Personas wanted in the room</Typography>
        <Typography variant="caption" color="text.secondary">
          Pick which of the campaign&apos;s personas this workshop wants and set each one&apos;s priority.
          Priority uses the wording above. Manage the persona catalogue itself in the References tab.
        </Typography>
        {personaOptions.length === 0 ? (
          <Typography variant="caption" color="warning.main">
            No personas defined yet — add them in the References tab.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            <Autocomplete
              multiple
              size="small"
              disableCloseOnSelect
              disabled={disabled}
              options={personaOptions}
              getOptionLabel={(p) => p.label || '(untitled persona)'}
              isOptionEqualToValue={(a, b) => personaSelectionRef(a) === personaSelectionRef(b)}
              value={personaOptions.filter((p) => isPersonaSelected(personaSelectionRef(p)))}
              onChange={(_e, nextPersonas) =>
                setPersonas({
                  // Rebuild the selection preserving each already-selected persona's priority; a
                  // newly-added persona defaults to its catalogue `role`.
                  selected: nextPersonas.map((p) => {
                    const ref = personaSelectionRef(p);
                    return {
                      ref,
                      priority: isPersonaSelected(ref) ? personaPriorityFor(ref) : p.role,
                    };
                  }),
                })
              }
              renderInput={(params) => (
                <TextField {...params} label="Personas wanted" placeholder="Add a persona" />
              )}
            />
            {selectedPersonas.length > 0 && (
              <Stack spacing={1}>
                {selectedPersonas.map((sel) => {
                  const persona = personaOptions.find(
                    (p) => personaSelectionRef(p) === sel.ref,
                  );
                  return (
                    <Stack
                      key={sel.ref}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography variant="body2">
                        {persona?.label || sel.ref || '(untitled persona)'}
                      </Typography>
                      <TextField
                        select
                        label="Priority"
                        size="small"
                        value={sel.priority}
                        disabled={disabled}
                        onChange={(e) =>
                          setPersonaPriority(sel.ref, e.target.value as PersonaRole)
                        }
                        sx={{ minWidth: 150 }}
                      >
                        {PERSONA_ROLES.map((r) => (
                          <MenuItem key={r.value} value={r.value}>
                            {r.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    );
  }

  // section === 'schedule'
  const durations = value.schedule.durationOptions;
  return (
    <Stack spacing={2}>
      <TextField
        label="Section heading"
        size="small"
        fullWidth
        disabled={disabled}
        value={value.schedule.title}
        onChange={(e) => setSchedule({ title: e.target.value })}
        helperText="Heading over the scheduling block (default “Schedule the workshop”)."
      />
      <TextField
        label="Section description"
        size="small"
        fullWidth
        multiline
        minRows={2}
        disabled={disabled}
        value={value.schedule.description}
        onChange={(e) => setSchedule({ description: e.target.value })}
        helperText="Lead paragraph under the heading."
      />
      <TextField
        label="Recording notice — bold lead"
        size="small"
        fullWidth
        disabled={disabled}
        value={value.schedule.recordingNoticeLead}
        onChange={(e) => setSchedule({ recordingNoticeLead: e.target.value })}
        helperText="Bolded first line of the recording/transcription notice."
      />
      <TextField
        label="Recording notice — body"
        size="small"
        fullWidth
        multiline
        minRows={2}
        disabled={disabled}
        value={value.schedule.recordingNoticeBody}
        onChange={(e) => setSchedule({ recordingNoticeBody: e.target.value })}
        helperText="Remaining text of the notice, shown after the bold lead."
      />
      <TextField
        label="Meeting lengths (minutes)"
        size="small"
        fullWidth
        disabled={disabled}
        value={durationsText}
        onChange={(e) => {
          setDurationsText(e.target.value);
          setSchedule({ durationOptions: parseDurations(e.target.value) });
        }}
        helperText="Comma-separated, in the order shown in the picker (default “90, 120, 150”)."
      />
      <TextField
        label="Default meeting length"
        select
        size="small"
        fullWidth
        disabled={disabled || durations.length === 0}
        value={durations.includes(value.schedule.defaultDurationMinutes)
          ? value.schedule.defaultDurationMinutes
          : ''}
        onChange={(e) => setSchedule({ defaultDurationMinutes: Number(e.target.value) })}
        helperText="Pre-selected length for a new booking. Choose one of the lengths above."
      >
        {durations.map((m) => (
          <MenuItem key={m} value={m}>
            {m} minutes
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="Meeting title template"
        size="small"
        fullWidth
        disabled={disabled}
        value={value.schedule.meetingTitleTemplate}
        onChange={(e) => setSchedule({ meetingTitleTemplate: e.target.value })}
        helperText="Default Webex meeting title. Use {account} for the account name."
      />
      <Divider />
      <TextField
        label="People-to-schedule note"
        size="small"
        fullWidth
        multiline
        minRows={2}
        disabled={disabled}
        value={value.schedule.peopleToScheduleNote}
        onChange={(e) => setSchedule({ peopleToScheduleNote: e.target.value })}
        helperText="Optional lead line for a “People to schedule” roster in the schedule block. Leave blank to hide the roster entirely (the shipped default)."
      />
      {/* Read-only preview of the roster the runtime block will list — the selected team roles +
          personas resolved to names, the same union `formatPeopleToSchedule` / `{{webex.people}}`
          use. Refining it means editing the team/persona selections above, not a parallel list. */}
      <Typography variant="caption" color="text.secondary">
        People to schedule:{' '}
        {peopleToScheduleSummary || '— no team roles or personas selected —'}
      </Typography>
    </Stack>
  );
}
